import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { analytics } from "./client";
import { getStoredConsent, setStoredConsent } from "./consent";
import { subFromToken } from "./identity";
import { selectSession } from "../session/selectors";

const ConsentContext = createContext(null);

/**
 * Holds the analytics consent decision for the CURRENT user and drives the
 * wrapper's consent gate. Consent is single-per-person (keyed by Cognito sub):
 *  - same user (incl. workspace switch / re-login) → keeps the choice, no prompt
 *  - different user → no stored choice → prompt shows
 * `decision` is "granted" | "denied" | null (undecided → prompt should show).
 */
export function ConsentProvider({ children }) {
  const session = useSelector(selectSession());
  const userId = session?.user?.sub || subFromToken(session?.token) || null;

  // Seed synchronously from storage so the very first render already has the
  // correct value (no one-frame flash of the prompt for users who consented).
  const [decision, setDecision] = useState(() => (userId ? getStoredConsent(userId) : null));
  const [promptOpen, setPromptOpen] = useState(false);
  const lastUserId = useRef(userId);

  // Re-sync synchronously when the user changes (login / switch / logout).
  if (userId !== lastUserId.current) {
    lastUserId.current = userId;
    setDecision(userId ? getStoredConsent(userId) : null);
  }

  // Apply the current decision to the analytics gate.
  useEffect(() => {
    if (decision === "granted") {
      analytics.grantConsent();
    } else {
      analytics.revokeConsent(); // undecided / denied / no user → opted out
    }
  }, [decision]);

  // Always record the decision (so the prompt can't become an unclosable loop when
  // the user id can't be resolved); persist it only when we have a user to key by.
  const grant = useCallback(() => {
    setDecision("granted");
    if (userId) setStoredConsent(userId, "granted");
  }, [userId]);

  const deny = useCallback(() => {
    setDecision("denied");
    if (userId) setStoredConsent(userId, "denied");
  }, [userId]);

  const openPrompt = useCallback(() => setPromptOpen(true), []);
  const closePrompt = useCallback(() => setPromptOpen(false), []);

  return (
    <ConsentContext.Provider
      value={{ decision, grant, deny, promptOpen, openPrompt, closePrompt }}
    >
      {children}
    </ConsentContext.Provider>
  );
}

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error("useConsent must be used within a <ConsentProvider>.");
  }
  return ctx;
}
